// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/giftCardWallet.ts
// 🔧 PURPOSE: Le gift card salvate sul telefono, senza account.
//
//             Per ogni carta il telefono tiene il codice e una CHIAVE (la
//             firma il channel manager, lib/gift-card-wallet): con le due il
//             server dice saldo, scadenza, condizioni e utilizzi
//             (POST /api/public/gift-cards/wallet). La chiave arriva:
//             - dal pulsante "Salva nell'app" dell'email (/gift-card?add=&k=);
//             - da "Aggiungi gift card" (codice + email, /wallet/add);
//             - dopo l'acquisto in app (GIFTCARD_CONFIRMED o /payment-success);
//             - per l'ospite collegato, dalle gift card della sua email (/mine).
//
//             Qui solo il salvataggio (localStorage, mai un errore se il
//             browser lo blocca) e l'ultima vista arrivata dal server, per
//             mostrare le carte anche senza rete. Chi cambia il portafoglio
//             avvisa con l'evento WALLET_EVENT (SpecialOffers conta le carte).
// ============================================

export type WalletCardStatus = 'active' | 'used' | 'expired' | 'cancelled' | 'pending';

export interface WalletHistoryEntry {
  /** "YYYY-MM-DD" (giorno di Roma). */
  date: string;
  amount: number;
  /** used = speso in una prenotazione; lost = resto perso (uso singolo); adjusted = correzione. */
  kind: 'used' | 'lost' | 'adjusted';
}

/** Quello che il server dice di una carta (CM lib/gift-card-wallet, WalletCardView). */
export interface WalletCardView {
  code: string;
  status: WalletCardStatus;
  initialValue: number;
  balance: number;
  /** "YYYY-MM-DD", ultimo giorno per prenotare; null = senza scadenza. */
  expiresOn: string | null;
  recipientFirstName: string;
  /** Le condizioni da leggere, già nella lingua chiesta. */
  conditions: string[];
  singleUse: boolean;
  recipientOnly: boolean;
  history: WalletHistoryEntry[];
}

export interface WalletEntry {
  code: string;
  key: string;
  /** ISO: quando è entrata nel portafoglio (le più recenti in cima). */
  addedAt: string;
  /** L'ultima vista arrivata dal server (anche senza rete si vede qualcosa). */
  card?: WalletCardView;
  /** Il server non la riconosce più (codice cancellato o chiave cambiata). */
  gone?: boolean;
}

const STORAGE_KEY = 'nva.giftCardWallet.v1';
export const WALLET_EVENT = 'nva:gift-card-wallet';
/** Quante carte al massimo (lo stesso tetto della rotta del server). */
export const WALLET_MAX = 20;

const CODE_RE = /^GC-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
const KEY_RE = /^[A-Za-z0-9_-]{32}$/;

export const normalizeGiftCardCode = (code: unknown): string => String(code ?? '').trim().toUpperCase();
export const isGiftCardCode = (code: unknown): boolean => CODE_RE.test(normalizeGiftCardCode(code));
export const isWalletKey = (key: unknown): key is string => typeof key === 'string' && KEY_RE.test(key);

const isEntry = (e: unknown): e is WalletEntry => {
  const x = e as WalletEntry;
  return !!x && typeof x === 'object' && isGiftCardCode(x.code) && isWalletKey(x.key);
};

export function loadWallet(): WalletEntry[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isEntry).slice(0, WALLET_MAX) : [];
  } catch {
    return [];
  }
}

function saveWallet(entries: WalletEntry[]): WalletEntry[] {
  const list = entries.slice(0, WALLET_MAX);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Spazio pieno o salvataggio bloccato: la lista resta per questa visita.
  }
  try {
    window.dispatchEvent(new Event(WALLET_EVENT));
  } catch {
    // niente
  }
  return list;
}

/**
 * Aggiunge (o aggiorna) una carta. Una carta già salvata resta al suo posto;
 * una nuova va in cima. Codice o chiave non validi: niente cambia.
 */
export function addToWallet(code: string, key: string, card?: WalletCardView): WalletEntry[] {
  const c = normalizeGiftCardCode(code);
  const list = loadWallet();
  if (!isGiftCardCode(c) || !isWalletKey(key)) return list;
  const i = list.findIndex((e) => e.code === c);
  if (i >= 0) {
    const next = [...list];
    next[i] = { ...next[i], key, ...(card ? { card, gone: false } : {}) };
    return saveWallet(next);
  }
  return saveWallet([{ code: c, key, addedAt: new Date().toISOString(), ...(card ? { card } : {}) }, ...list]);
}

export function removeFromWallet(code: string): WalletEntry[] {
  const c = normalizeGiftCardCode(code);
  return saveWallet(loadWallet().filter((e) => e.code !== c));
}

/** Le risposte del server per carta: aggiorna le viste, segna quelle che non riconosce più. */
export function applyWalletResults(
  results: Array<{ code: string; card?: WalletCardView; error?: string }>,
): WalletEntry[] {
  const byCode = new Map(results.map((r) => [normalizeGiftCardCode(r.code), r]));
  const next = loadWallet().map((e) => {
    const r = byCode.get(e.code);
    if (!r) return e;
    if (r.card) return { ...e, card: r.card, gone: false };
    if (r.error === 'invalid_key' || r.error === 'not_found') return { ...e, gone: true };
    return e;
  });
  return saveWallet(next);
}

/** Carte da mostrare come "utilizzabili" (per il contatore della Home). */
export const usableWalletCount = (entries: WalletEntry[]): number =>
  entries.filter((e) => !e.gone && (!e.card || e.card.status === 'active' || e.card.status === 'pending')).length;
